import { useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Activity } from "lucide-react";
import { api } from "@/lib/api";
import { timeAgo, ACTION_LABELS } from "@/lib/constants";
import { SpaceSection } from "@/components/SpaceSection";
import { UserAvatar } from "@/components/Avatars";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function ActivityPage() {
  const { id: workspaceId } = useParams();
  const [actor, setActor] = useState("all");
  const [action, setAction] = useState("all");

  const { data: members = [] } = useQuery({
    queryKey: ["members", workspaceId],
    queryFn: async () => (await api.get(`/workspaces/${workspaceId}/members`)).data,
  });
  const { data: entries = [] } = useQuery({
    queryKey: ["activity", workspaceId, actor, action],
    queryFn: async () => {
      const params = {};
      if (actor !== "all") params.actor_id = actor;
      if (action !== "all") params.action = action;
      return (await api.get(`/workspaces/${workspaceId}/activity`, { params })).data;
    },
  });

  const memberColor = (pid) => members.find((m) => m.profile_id === pid)?.avatar_color || "#5F6368";

  return (
    <SpaceSection title="Actividad">
      <div className="flex flex-wrap gap-2">
        <Select value={actor} onValueChange={setActor}>
          <SelectTrigger className="h-8 w-44 text-xs" data-testid="activity-actor-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas las personas</SelectItem>
            {members.map((m) => <SelectItem key={m.profile_id} value={m.profile_id}>{m.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={action} onValueChange={setAction}>
          <SelectTrigger className="h-8 w-48 text-xs" data-testid="activity-action-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas las acciones</SelectItem>
            {Object.entries(ACTION_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      {entries.length === 0 ? (
        <Card className="p-12 text-center border-dashed">
          <Activity className="h-8 w-8 mx-auto text-muted-foreground mb-3" />
          <p className="text-sm text-muted-foreground">Sin actividad para este filtro.</p>
        </Card>
      ) : (
        <Card className="divide-y divide-border" data-testid="activity-feed">
          {entries.map((a) => (
            <div key={a.id} className="p-3.5 flex items-center gap-3 text-sm" data-testid={`activity-row-${a.id}`}>
              <UserAvatar name={a.actor_name} color={memberColor(a.actor_profile_id)} size="sm" />
              <p className="flex-1 min-w-0">
                <span className="font-semibold">{a.actor_name}</span>{" "}
                <span className="text-muted-foreground">{ACTION_LABELS[a.action] || a.action}</span>
                {a.metadata?.name ? <span className="font-medium"> "{a.metadata.name}"</span> : null}
                {a.metadata?.member_name ? <span className="font-medium"> · {a.metadata.member_name}</span> : null}
              </p>
              <span className="text-xs text-muted-foreground shrink-0">{timeAgo(a.created_at)}</span>
            </div>
          ))}
        </Card>
      )}
    </SpaceSection>
  );
}
